/* eslint-disable default-case */
import React, { useState, useCallback, useMemo } from "react";
import { AdminDetailFieldLabel } from "../../../AdminDashboard/shared/adminUx";
import DropzoneUploader from "../../../../components/common/DropzoneUploader/DropzoneUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const ImageContainer = React.memo(({ url, name, isDragging }) => (
  <div className="relative size-full">
    <div 
      className="size-full bg-cover bg-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200"
      style={{ 
        backgroundImage: `url(${url})`,
        opacity: isDragging ? 0.5 : 1
      }}
    />
    <div className="absolute inset-0 bg-transparent" />
  </div>
));

const SortableItem = ({ id, item, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }), [transform, transition, isDragging]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative group">
        <div className="size-[60px]">
          <ImageContainer 
            url={item.url || ''} 
            name={item?.file?.name || 'Uploaded image'}
            isDragging={isDragging}
          />
          <button 
            type="button" 
            onClick={() => onDelete(index)}
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <i className="fi fi-rs-cross text-[10px] text-offblack"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

const PortfolioUploading = ({ setImageList, ImageList = [], maxFiles = 2 }) => {
  const [images, setImages] = useState(Array.isArray(ImageList) ? ImageList : []);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleUploadComplete = useCallback((uploadData) => {
    const newImages = Array.isArray(uploadData) ? uploadData : [uploadData];
    const processedImages = newImages.map((data, index) => ({
      // file: data.file,
      name: data.fileName,
      url: data.url,
      // order: images.length + index
    }));
    
    setImages(prev => [...prev, ...processedImages]);
    
    if (typeof setImageList === 'function') {
      setImageList(prev => {
        const currentList = Array.isArray(prev) ? prev : [];
        return [...currentList, ...processedImages];
      });
    }
  }, [images.length, setImageList]);

  const deleteImage = useCallback((index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    if (typeof setImageList === 'function') {
      setImageList(newImages);
    }
  }, [images, setImageList]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = images.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = images.findIndex((_, index) => `item-${index}` === over.id);
      
      const newItems = arrayMove(images, oldIndex, newIndex);
      
      setImages(newItems);
      if (typeof setImageList === 'function') {
        setImageList(newItems);
      }
    }
    
    setIsDragging(false);
    setActiveId(null);
  }, [images, setImageList]);

  const items = useMemo(() => 
    images.map((_, index) => `item-${index}`),
    [images]
  );

  return (
    <>
      <div className="w-full mt-5">
        <div className="flex">
          <label className="block mb-2 flex items-start gap-1">
            <AdminDetailFieldLabel className="text-offblack">Post Pictures</AdminDetailFieldLabel>
            <i className="fi fi-rs-medical-star text-offblack text-[6px] mt-0.5" aria-hidden />
          </label>
        </div>

        {/* Uploaded Images */}
        {images.length > 0 && (
          <div className="mt-1 mb-5 w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-7">
                  {images.map((item, index) => (
                    <SortableItem
                      key={`item-${index}`}
                      id={`item-${index}`}
                      item={item}
                      index={index}
                      onDelete={deleteImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        <DropzoneUploader
          isImage={true}
          maxFiles={maxFiles}
          loadingType="progress"
          onUploadComplete={handleUploadComplete}
          currentFileCount={images.length}
          dashboardUpload
        />
      </div>
    </>
  );
};

export default PortfolioUploading;
